import type { ComponentType, ReactNode } from "react";
import styles from "./ModuleHeader.module.css";

export type ModuleHeaderAccent = "email" | "calendar" | "files" | "review" | "knowledge" | "deckorate";

interface ModuleHeaderProps {
  readonly title: string;
  readonly subtitle?: ReactNode;
  readonly icon?: ComponentType;
  readonly accent?: ModuleHeaderAccent;
  readonly onBack?: () => void;
  /** Accessible name of the back button, for sub-screens whose back isn't the modules grid. */
  readonly backLabel?: string;
  /** Right-aligned header controls, e.g. a module's "+ New" link. */
  readonly actions?: ReactNode;
}

const ACCENT_CLASS: Record<ModuleHeaderAccent, string> = {
  email: styles.iconEmail!,
  calendar: styles.iconCalendar!,
  files: styles.iconFiles!,
  review: styles.iconReview!,
  knowledge: styles.iconKnowledge!,
  deckorate: styles.iconDeckorate!,
};

export function ModuleHeader({
  title,
  subtitle,
  icon: Icon,
  accent,
  onBack,
  backLabel = "Back to modules",
  actions,
}: Readonly<ModuleHeaderProps>) {
  const iconClass = accent
    ? `${styles.icon} ${ACCENT_CLASS[accent]}`
    : styles.icon;

  return (
    <div className={styles.head}>
      {onBack && (
        <button
          type="button"
          className={styles.back}
          onClick={onBack}
          aria-label={backLabel}
        >
          {"←"}
        </button>
      )}
      {Icon && (
        <div className={iconClass}>
          <Icon />
        </div>
      )}
      <div className={styles.text}>
        <h2 className={styles.title}>{title}</h2>
        {subtitle != null && <p className={styles.subtitle}>{subtitle}</p>}
      </div>
      {actions != null && <div className={styles.actions}>{actions}</div>}
    </div>
  );
}
